import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ContactGroupService } from 'src/app/private/services/contact-group.service';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-bulk-import-dialing-content',
  imports: [SharedModule],
  templateUrl: './bulk-import-dialing-content.component.html',
  styleUrl: './bulk-import-dialing-content.component.scss'
})
export class BulkImportDialingContentComponent {

  csvFile: File | null = null;
  btnLoading = false;

  @Input() selectedGroupId: any;
  @Output() reload: EventEmitter<any> = new EventEmitter();
  @Output() closeDialog: EventEmitter<any> = new EventEmitter();

  constructor(
    private api: ContactGroupService,
    private localStorage: LocalStorageService,
    private messageService: MessageService
  ) { }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        this.csvFile = file;
        // Create a dummy event object that matches p-fileUpload's expected format
        this.onFileSelect({ files: [file] });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please upload a CSV file'
        });
      }
    }
  }

  onFileSelect(event: any) {
    if (event.files && event.files.length > 0) {
      const file = event.files[0];
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        this.csvFile = file;
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please upload a CSV file'
        });
      }
    }
  }

  clearFile(fileUpload: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.csvFile = null;
    fileUpload.clear();
  }

  downloadSampleCSV() {
    // Sample CSV data with headers for dialing group contacts
    const csvContent = `mobile_number,name,email
+1234567890,John Doe,john.doe@example.com
+0987654321,Jane Smith,jane.smith@example.com
+1122334455,Mike Johnson,mike.johnson@example.com`;

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'sample-dialing-group-contacts.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Download Started',
      detail: 'Sample CSV file has been downloaded'
    });
  }

  handleCloseDialog() {
    this.closeDialog.emit();
  }

  uploadFile() {
    if (!this.csvFile) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please select a file first' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const fileContent = reader.result as string;

      // Check if the file is empty or has only headers
      const lines = fileContent.split('\n').filter(line => line.trim());
      if (lines.length <= 1) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'The CSV file is empty. Please add at least one row of data.'
        });
        return;
      }

      const payload = {
        organizationId: this.localStorage.getOrganizationId(),
        campusId: this.localStorage.getCampusId(), // make sure you have this
        groupId: this.selectedGroupId,
        fileContent // CSV content as string
      };

      this.btnLoading = true;
      this.api.uploadContactCSV(payload).subscribe(
        (response: any) => {
          this.btnLoading = false;
          this.reload.emit();
          this.handleCloseDialog();
          this.messageService.add({ severity: 'success', summary: 'Success', detail: response.message });
        },
        (error: any) => {
          console.error(error);
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload file' });
          this.btnLoading = false;
        }
      );
    };

    reader.onerror = (err) => {
      console.error('File read error:', err);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to read file' });
    };

    reader.readAsText(this.csvFile); // read CSV as text
  }


  onFileClear() {
    this.csvFile = null;
  }

  onFileRemove(event: any) {
    this.csvFile = null;
  }
}
